/**
 * Serviço de cache para armazenar dados no sessionStorage
 * Cache expira após 30 minutos
 */

const CACHE_EXPIRATION_MS = 30 * 60 * 1000; // 30 minutos

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

/**
 * Verifica se uma entrada de cache é válida (não expirada)
 */
function isCacheValid(timestamp: number): boolean {
  const now = Date.now();
  return (now - timestamp) < CACHE_EXPIRATION_MS;
}

/**
 * Obtém uma entrada do cache
 */
function getCachedEntry<T>(key: string): CacheEntry<T> | null {
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;

    const entry: CacheEntry<T> = JSON.parse(cached);
    
    if (!isCacheValid(entry.timestamp)) {
      sessionStorage.removeItem(key);
      return null;
    }

    return entry;
  } catch (error) {
    console.error(`Error reading cache for key ${key}:`, error);
    sessionStorage.removeItem(key);
    return null;
  }
}

/**
 * Salva uma entrada no cache
 */
function setCachedEntry<T>(key: string, data: T): void {
  try {
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch (error) {
    console.error(`Error writing cache for key ${key}:`, error);
  }
}

/**
 * Remove uma entrada do cache
 */
function removeCachedEntry(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing cache for key ${key}:`, error);
  }
}

export const cacheService = {
  // Permissões
  getCachedPermissions(organizationId: string, userId: string): Record<string, boolean> | null {
    const key = `permissions_${organizationId}_${userId}`;
    const entry = getCachedEntry<Record<string, boolean>>(key);
    return entry?.data ?? null;
  },

  setCachedPermissions(organizationId: string, userId: string, permissions: Record<string, boolean>): void {
    const key = `permissions_${organizationId}_${userId}`;
    setCachedEntry(key, permissions);
  },

  clearPermissionsCache(organizationId?: string, userId?: string): void {
    if (organizationId && userId) {
      const key = `permissions_${organizationId}_${userId}`;
      removeCachedEntry(key);
    } else {
      // Limpar todas as permissões
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('permissions_')) {
          removeCachedEntry(key);
        }
      });
    }
  },

  // Perfil
  getCachedProfile(userId: string): any | null {
    const key = `profile_${userId}`;
    const entry = getCachedEntry<any>(key);
    return entry?.data ?? null;
  },

  setCachedProfile(userId: string, profile: any): void {
    const key = `profile_${userId}`;
    setCachedEntry(key, profile);
  },

  clearProfileCache(userId?: string): void {
    if (userId) {
      const key = `profile_${userId}`;
      removeCachedEntry(key);
    } else {
      // Limpar todos os perfis
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('profile_')) {
          removeCachedEntry(key);
        }
      });
    }
  },

  // Repositórios
  getCachedRepositories(organizationId: string): any[] | null {
    const key = `repositories_${organizationId}`;
    const entry = getCachedEntry<any[]>(key);
    return entry?.data ?? null;
  },

  setCachedRepositories(organizationId: string, repositories: any[]): void {
    const key = `repositories_${organizationId}`;
    setCachedEntry(key, repositories);
  },

  clearRepositoriesCache(organizationId?: string): void {
    if (organizationId) {
      const key = `repositories_${organizationId}`;
      removeCachedEntry(key);
    } else {
      // Limpar todos os repositórios
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('repositories_')) {
          removeCachedEntry(key);
        }
      });
    }
  },

  // Teams
  getCachedTeams(organizationId: string): any[] | null {
    const key = `teams_${organizationId}`;
    const entry = getCachedEntry<any[]>(key);
    return entry?.data ?? null;
  },

  setCachedTeams(organizationId: string, teams: any[]): void {
    const key = `teams_${organizationId}`;
    setCachedEntry(key, teams);
  },

  clearTeamsCache(organizationId?: string): void {
    if (organizationId) {
      const key = `teams_${organizationId}`;
      removeCachedEntry(key);
    } else {
      // Limpar todos os teams
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('teams_')) {
          removeCachedEntry(key);
        }
      });
    }
  },

  // Limpar todo o cache
  clearCache(): void {
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error('Error clearing cache:', error);
    }
  },
};
